import { useState } from 'react';

export default function FinalCTASection() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyCondition: '',
    estimatedValue: '',
    propertyAddress: '',
    transactionalConsent: false,
    marketingConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formDataToSend = new URLSearchParams();
      
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'transactionalConsent' || key === 'marketingConsent') {
          formDataToSend.append(key, value ? 'Yes' : 'No');
        } else {
          formDataToSend.append(key, value.toString());
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d5rkocetc768qf4bqkmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you! We\'ll contact you within 24 hours with a cash offer.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          propertyCondition: '',
          estimatedValue: '',
          propertyAddress: '',
          transactionalConsent: false,
          marketingConsent: false,
        });
      } else {
        setSubmitMessage('Something went wrong. Please call us directly at (915) 206-5814.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please call us directly at (915) 206-5814.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  return (
    <div className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Sell Your Distressed Property?
          </h2>
          <p className="text-xl text-gray-300 mb-4">
            Call us at <a href="tel:9152065814" className="text-teal-400 font-bold hover:text-teal-300 cursor-pointer whitespace-nowrap">(915) 206-5814</a> to speak with us directly
          </p>
          <p className="text-gray-300 text-lg">Or submit your property below to get a cash offer within 24 hours</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <form
            className="space-y-5"
            data-readdy-form
            id="distressed-property-final-form"
            onSubmit={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
              <div>
                <select
                  name="propertyCondition"
                  required
                  value={formData.propertyCondition}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all bg-white cursor-pointer"
                >
                  <option value="">Property condition?</option>
                  <option value="excellent">Excellent - Move-in Ready</option>
                  <option value="good">Good - Minor Repairs</option>
                  <option value="fair">Fair - Moderate Repairs</option>
                  <option value="poor">Poor - Major Repairs</option>
                  <option value="uninhabitable">Uninhabitable</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <input
                  type="text"
                  name="estimatedValue"
                  placeholder="Estimated Value (Optional)"
                  value={formData.estimatedValue}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="propertyAddress"
                  placeholder="Property Address"
                  required
                  value={formData.propertyAddress}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="distressed-transactionalConsent"
                  name="transactionalConsent"
                  required
                  checked={formData.transactionalConsent}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="distressed-transactionalConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                  <span className="text-red-500">*</span> I consent to receive transactional messages from TREX RE LLC to the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="distressed-marketingConsent"
                  name="marketingConsent"
                  checked={formData.marketingConsent}
                  onChange={handleChange}
                  className="mt-1 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <label htmlFor="distressed-marketingConsent" className="ml-3 text-xs text-gray-600 leading-relaxed cursor-pointer">
                  I consent to receive marketing and promotional messages from TREX RE LLC at the phone number provided. Message frequency may vary. Message and Data rates may apply. Reply HELP for help or STOP to opt-out.
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? 'Submitting...' : 'Get My Cash Offer Now'}
            </button>
          </form>

          {submitMessage && (
            <div className={`mt-4 p-4 rounded-lg ${submitMessage.includes('Thank you') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {submitMessage}
            </div>
          )}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="text-white">
            <div className="text-4xl font-bold text-teal-400 mb-2">300+</div>
            <p className="text-gray-300">Properties Purchased</p>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold text-teal-400 mb-2">10 Days</div>
            <p className="text-gray-300">Average Close Time</p>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold text-teal-400 mb-2">98%</div>
            <p className="text-gray-300">Customer Satisfaction</p>
          </div>
        </div>
      </div>
    </div>
  );
}